import { aiHeaders } from '../lib/photoAi';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Sparkles, Ruler, PenLine, Trash2, Check, Loader2, Undo2 } from 'lucide-react';
import { aiToken } from '../lib/license';
import Loupe, { cvPoint, cvScale } from './Loupe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/leak/measure`;
const shoelace = (P) => Math.abs(P.reduce((s, p, i) => { const q = P[(i + 1) % P.length]; return s + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;
const loadImg = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => { const im = new Image(); im.onload = () => { const k = Math.min(1, 1600 / Math.max(im.width, im.height)), c = document.createElement('canvas'); c.width = im.width * k; c.height = im.height * k; c.getContext('2d').drawImage(im, 0, 0, c.width, c.height); res(c.toDataURL('image/jpeg', 0.85)); }; im.src = r.result; }; r.readAsDataURL(file); });

// photo with folding rule: AI (or manual 2-point) scale + damage contour -> area in mm²
export default function LeakPhoto({ t, onClose, onDone }) {
  const [img, setImg] = useState(null);
  const [ruler, setRuler] = useState([]);
  const [mm, setMm] = useState('100');
  const [poly, setPoly] = useState([]);
  const [mode, setMode] = useState('ruler');
  const [busy, setBusy] = useState(false);
  const cv = useRef(null), imRef = useRef(null), inRef = useRef(null);
  const scale = ruler.length === 2 && +mm > 0 ? +mm / Math.hypot(ruler[1][0] - ruler[0][0], ruler[1][1] - ruler[0][1]) : null;
  const area = scale && poly.length > 2 ? shoelace(poly) * scale * scale : null;
  useEffect(() => {
    const c = cv.current, im = imRef.current;
    if (!c || !im) return;
    c.width = im.width; c.height = im.height;
    const g = c.getContext('2d'), r = c.getBoundingClientRect(), ks = r.width ? Math.min(r.width / im.width, r.height / im.height) : 0, lw = Math.max(2, ks ? 2.5 / ks : Math.max(im.width, im.height) / 300);
    g.drawImage(im, 0, 0);
    if (poly.length) { g.beginPath(); poly.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.closePath(); g.fillStyle = 'rgba(220,38,38,0.3)'; g.fill(); g.strokeStyle = '#dc2626'; g.lineWidth = lw; g.stroke(); }
    if (ruler.length) { g.strokeStyle = '#facc15'; g.lineWidth = lw * 1.5; g.beginPath(); g.moveTo(...ruler[0]); g.lineTo(...(ruler[1] || ruler[0])); g.stroke(); ruler.forEach((p) => { g.beginPath(); g.arc(p[0], p[1], lw * 3, 0, 7); g.fillStyle = '#facc15'; g.fill(); }); }
    if (area) { g.font = `bold ${lw * 9}px sans-serif`; g.fillStyle = '#fff'; g.strokeStyle = '#000'; g.lineWidth = lw; const s = `${Math.round(area)} mm²  (${mm} mm ≙ ${t.lkRuler})`; g.strokeText(s, lw * 6, lw * 14); g.fillText(s, lw * 6, lw * 14); }
  }, [img, ruler, poly, area, mm, t]);
  const pick = async (f) => { if (!f) return; const d = await loadImg(f); const im = new Image(); im.onload = () => { imRef.current = im; setImg(d); setRuler([]); setPoly([]); }; im.src = d; };
  const at = (e) => cvPoint(cv.current, e);
  const drag = useRef(null), mmRef = useRef(null), focusMm = useRef(false);
  const [lp, setLp] = useState(null);
  const down = (e) => {
    e.preventDefault();
    const p = at(e), c = cv.current, rad = 22 / cvScale(c);
    const near = (arr) => arr.findIndex((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < rad);
    const ri = near(ruler), pi = near(poly);
    c.setPointerCapture?.(e.pointerId);
    setLp({ p: ri >= 0 ? ruler[ri] : pi >= 0 ? poly[pi] : p, at: [e.clientX, e.clientY] });
    if (ri >= 0 && (mode === 'ruler' || pi < 0)) { drag.current = ['r', ri]; return; }
    if (pi >= 0) { drag.current = ['p', pi]; return; }
    if (mode === 'ruler') {
      const n = ruler.length >= 2 ? [p] : [...ruler, p];
      setRuler(n); drag.current = ['r', n.length - 1]; focusMm.current = n.length === 2;
    } else { setPoly((v) => [...v, p]); drag.current = ['p', poly.length]; }
  };
  const move = (e) => {
    const d = drag.current; if (!d) return;
    const p = at(e), set = d[0] === 'r' ? setRuler : setPoly;
    set((v) => v.map((q, i) => (i === d[1] ? p : q)));
    setLp({ p, at: [e.clientX, e.clientY] });
  };
  const up = () => { drag.current = null; setLp(null); if (focusMm.current) { focusMm.current = false; setTimeout(() => { mmRef.current?.focus(); mmRef.current?.select(); }, 0); } };
  const ai = async () => {
    setBusy(true);
    try {
      const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-License-Token': await aiToken(), ...aiHeaders() }, body: JSON.stringify({ image_base64: img, lang: localStorage.getItem('baustelle_app_lang') || 'de' }) });
      if (!res.ok) throw new Error(res.status);
      const d = await res.json(), W = imRef.current.width, H = imRef.current.height, P = ([y, x]) => [(x / 1000) * W, (y / 1000) * H];
      if (d.ruler?.a && d.ruler?.b) { setRuler([P(d.ruler.a), P(d.ruler.b)]); setMm(String(d.ruler.mm || 100)); }
      if (d.contour?.length > 2) setPoly(d.contour.map(P));
      toast[d.contour?.length > 2 && d.ruler?.a ? 'success' : 'warning'](d.contour?.length > 2 && d.ruler?.a ? t.lkAiOk : t.lkAiPartial);
    } catch { toast.error(t.lkAiErr); }
    setBusy(false);
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="leak-photo-dialog">
      <div className="editor leak-photo">
        <div className="editor-head"><h4>📸 {t.lkPhotoBtn}</h4><button className="icon-btn" data-testid="leak-photo-close" onClick={onClose}><X size={16} /></button></div>
        <p className="muted small-hint">{t.lkPhotoHint}</p>
        <input ref={inRef} type="file" accept="image/*" capture="environment" hidden data-testid="leak-photo-input" onChange={(e) => pick(e.target.files?.[0])} />
        {!img && <button className="btn btn-dark" data-testid="leak-photo-take" onClick={() => inRef.current.click()}>📸 {t.lkTake}</button>}
        {img && (
          <>
            <canvas ref={cv} className="leak-cv" data-testid="leak-photo-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
            {lp && <Loupe src={cv} p={lp.p} at={lp.at} />}
            <div className="row lk-row">
              <button className="btn btn-proj btn-sm" data-testid="leak-ai-btn" disabled={busy} onClick={ai}>{busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {t.lkAi}</button>
              <button className={`chip seg ${mode === 'ruler' ? 'on' : ''}`} data-testid="leak-mode-ruler" onClick={() => setMode('ruler')}><Ruler size={13} /> {t.lkModeRuler}</button>
              <label className="lk-mm">= <input ref={mmRef} inputMode="decimal" data-testid="leak-ruler-mm" value={mm} onChange={(e) => setMm(e.target.value.replace(',', '.'))} onKeyDown={(e) => e.key === 'Enter' && setMode('contour')} /> mm</label>
              <button className={`chip seg ${mode === 'contour' ? 'on' : ''}`} data-testid="leak-mode-contour" onClick={() => setMode('contour')}><PenLine size={13} /> {t.lkModeContour}</button>
              {poly.length > 0 && <button className="icon-btn" data-testid="leak-undo-btn" onClick={() => setPoly((v) => v.slice(0, -1))}><Undo2 size={14} /></button>}
              <button className="icon-btn" data-testid="leak-clear-btn" onClick={() => { setPoly([]); setRuler([]); }}><Trash2 size={14} /></button>
            </div>
            <p data-testid="leak-photo-result"><b>{area ? `${Math.round(area)} mm²` : '–'}</b> {!scale && <small className="muted">{t.lkNeedRuler}</small>}</p>
            <button className="btn btn-green" data-testid="leak-photo-apply" disabled={!area} onClick={() => onDone(area, cv.current.toDataURL('image/jpeg', 0.85))}><Check size={14} /> {t.lkApply}</button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
